import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

// Getter to supply color to start border and icon
const getColor = (flavor) => {
  if (flavor === 'caution') {
    return 'var(--cc_color_utility_caution)';
  } else if (flavor === 'destroy') {
    return 'var(--cc_color_utility_destroy)';
  } else if (flavor === 'success') {
    return 'var(--cc_color_utility_success)';
  } else if (flavor === 'pro') {
    return 'var(--cc_color_utility_upgrade)';
  } else {
    return 'var(--cc_color_brand_primary)';
  }
};

//
// Icon
//
const getIconName = (iconName, flavor) => {
  if (iconName) {
    return iconName;
  }

  if (flavor === 'caution' || flavor === 'destroy') {
    return 'alert-circle';
  } else if (flavor === 'success') {
    return 'check-circle';
  } else if (flavor === 'pro') {
    return 'flash-circle';
  } else {
    return 'information-outline';
  }
};

const getIconSize = (layout, flavor) => {
  if (layout === 'inline-mini') {
    return '18px';
  } else if (flavor === 'pro' && layout === 'toast') {
    return '24px';
  } else {
    return '22px';
  }
};

const Icon = styled.i`
  font-size: ${(props) => getIconSize(props.layout, props.flavor)};
  margin-inline-end: 12px;
  color: ${(props) => getColor(props.flavor)};
`;
//
// end Icon
//

//
// Outermost container
//

const getPadding = (layout, showCloseIcon, flavor, showBorder) => {
  if (layout === 'toast' && showCloseIcon) {
    return `16px 12px 16px ${flavor === 'pro' ? '16px' : '24px'}`;
  } else if (layout === 'inline-mini') {
    return `8px 12px 8px ${showBorder ? '16px' : '12px'}`;
  } else if (flavor === 'pro' && layout === 'toast') {
    return '12px 16px';
  } else if (layout === 'inline') {
    return '12px 16px';
  } else {
    return '16px';
  }
};

const getBgColor = (layout, flavor) => {
  if (flavor === 'caution') {
    return 'var(--cc_color_utility_caution_alpha_10)';
  } else if (flavor === 'destroy') {
    return 'var(--cc_color_utility_destroy_alpha_10)';
  } else if (flavor === 'success') {
    return 'var(--cc_color_utility_success_alpha_10)';
  } else if (flavor === 'pro') {
    return 'var(--cc_color_utility_info)';
  } else if (layout === 'toast') {
    return 'white';
  } else {
    return 'var(--cc_color_brand_primary_alpha_10)';
  }
};

const sharedStyles = css`
  position: relative;
  display: flex;
  align-items: center;
  background-color: ${(props) => getBgColor(props.layout, props.flavor)};
  padding: ${(props) =>
    getPadding(
      props.layout,
      props.showCloseIcon,
      props.flavor,
      props.showBorder,
    )};

  ${(props) =>
    props.layout === 'toast' &&
    `border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15)
  `}

  ${(props) =>
    props.showBorder &&
    `&::before {
    content: '';
    position: absolute;
    inset-block-start: 0;
    inset-block-end: 0;
    inset-inline-start: 0;
    width: 4px;
    background-color: ${getColor(props.flavor)};
  }`}
`;

/* Container defaults to rendering as div */
const Container = styled.div`
  ${sharedStyles}
`;

/* Define CloseIcon above container, so we can create :hover effect */
const CloseIcon = styled.i`
  color: ${(props) =>
    props.flavor === 'pro'
      ? 'var(--cc_color_button_text_primary)'
      : 'var(--cc_color_text_default)'};
  font-size: 18px;
  flex-shrink: 0;
  align-self: flex-start;
  margin-inline-start: 24px;
  margin-block-start: -2px;
  transition: 0.2s;
`;

/* if onClick is provided, container renders as a button */
const ContainerButton = styled.button`
  display: block;
  text-align: start;
  margin: 0;
  cursor: pointer;
  appearance: none;
  user-select: none;
  touch-action: manipulation;
  ${sharedStyles}

  &:hover ${CloseIcon} {
    transform: scale(1.2);
  }
`;

const handleClick = (e, onClick) => {
  if (typeof onClick === 'function') {
    onClick(e);
  }
};

const getElement = (onClick) => {
  if (onClick) {
    return ContainerButton;
  } else {
    return Container;
  }
};
//
// End outermost container
//

//
// Styles for main content area
//
const Message = styled.div`
  flex: 1;
  font-size: ${(props) => (props.layout === 'inline-mini' ? '12px' : '14px')};
  color: ${(props) =>
    props.flavor === 'pro'
      ? 'var(--cc_color_button_text_primary)'
      : 'var(--cc_color_text_default)'};
  line-height: ${(props) => (props.layout === 'inline-mini' ? '1.25' : '1.4')};
`;

const Heading = styled.h3`
  margin: 0 0 ${(props) => (props.layout === 'inline-mini' ? '1px' : '4px')};
  color: ${(props) =>
    props.flavor === 'pro'
      ? 'var(--cc_color_button_text_primary)'
      : 'var(--cc_color_text_default)'};
  line-height: 1.2;
  font-weight: bold;
  font-size: ${(props) => (props.layout === 'inline-mini' ? '12px' : '16px')};
`;
//
// End styles for main content area
//

const Alert = ({
  children,
  className,
  flavor,
  heading,
  iconName,
  layout,
  onClick,
  showBorder,
  showCloseIcon,
  style,
}) => {
  const Element = getElement(onClick);
  const isToast = layout === 'toast';

  return (
    <Element
      layout={layout}
      flavor={flavor}
      onClick={onClick ? (e) => handleClick(e, onClick) : null}
      showBorder={showBorder}
      showCloseIcon={showCloseIcon}
      className={className}
      style={style}
    >
      {!isToast || (isToast && flavor === 'pro') ? (
        <Icon
          layout={layout}
          flavor={flavor}
          className={`mdi mdi-${getIconName(iconName, flavor)}`}
        />
      ) : null}
      <Message layout={layout} flavor={flavor}>
        {heading ? (
          <Heading layout={layout} flavor={flavor}>
            {heading}
          </Heading>
        ) : null}
        {children}
      </Message>
      {isToast && showCloseIcon ? (
        <CloseIcon flavor={flavor} className="mdi mdi-close" />
      ) : null}
    </Element>
  );
};

Alert.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  flavor: PropTypes.oneOf(['caution', 'destroy', 'info', 'pro', 'success']),
  heading: PropTypes.node,
  iconName: PropTypes.string,
  layout: PropTypes.oneOf(['inline', 'inline-mini', 'toast']),
  onClick: PropTypes.func,
  showBorder: PropTypes.bool,
  showCloseIcon: PropTypes.bool,
  style: PropTypes.object,
};

Alert.defaultProps = {
  children: undefined,
  className: undefined,
  flavor: 'info',
  heading: undefined,
  iconName: undefined,
  layout: 'inline',
  onClick: undefined,
  showCloseIcon: false,
  style: undefined,
};

export default Alert;
